class VideoStream {
  private readonly width: number;
  private readonly height: number;
  private readonly videoDeviceIndex: number;
  private readonly videoDeviceTrackIndex: number;
  private videoTrack: MediaStreamTrack | undefined;
  private imageToProcess: null;
  private videoNode: any;

  constructor(width: number, height: number, videoDeviceIndex: number, videoDeviceTrackIndex: number) {
    this.width = width;
    this.height = height;
    this.videoDeviceIndex = videoDeviceIndex;
    this.videoDeviceTrackIndex = videoDeviceTrackIndex;
    this.imageToProcess = null;

    this.videoNode = null;
  }

  setVideoNode = (video: any) => {
    this.videoNode = video;
  };

  getTrack = () => {
    return this.videoTrack;
  };

  grabFrame = async () => {
    // eslint-disable-next-line
    // @ts-ignore
    const imageCapture = new ImageCapture(this.getTrack());
    // const capabilities = await imageCapture.getPhotoCapabilities();
    // console.log('Photo capabilities', capabilities);
    // const settings = await imageCapture.getPhotoSettings();
    // console.log('Photo settings', settings);

    this.imageToProcess = await imageCapture.grabFrame();
  };

  grabThermalFrame = async (webgl: any, grabFrameAdditionally = false) => {
    if (grabFrameAdditionally) {
      // eslint-disable-next-line
      // @ts-ignore
      const imageCapture = new ImageCapture(this.getTrack());
      await imageCapture.grabFrame();
    }

    // makes the first texture active
    webgl.activeTexture(webgl.TEXTURE0);
    // bind texture to webgl
    webgl.bindTexture(webgl.TEXTURE_2D, webgl.customProps.depthTexture);

    webgl.texImage2D(
      webgl.TEXTURE_2D,
      0,
      webgl.R32F,
      this.videoNode.width,
      this.videoNode.height,
      0,
      webgl.RED,
      webgl.FLOAT,
      this.videoNode,
    );

    webgl.bindFramebuffer(webgl.FRAMEBUFFER, webgl.customProps.frameBuffer);
    webgl.readPixels(
      0,
      0,
      this.videoNode.width,
      this.videoNode.height,
      webgl.customProps.readFormat,
      webgl.FLOAT,
      webgl.customProps.readBuffer,
    );
    webgl.bindFramebuffer(webgl.FRAMEBUFFER, null);

    let outData = null;
    if (webgl.customProps.readFormat === webgl.RED) {
      outData = [...webgl.customProps.readBuffer].map((i) => i * 65535);
    } else {
      const channelCount = 4;
      let j = 0;

      outData = new Array(this.videoNode.width * this.videoNode.height).fill(0);
      for (let i = 0; i < webgl.customProps.readBuffer.length; i += 4, j += channelCount) {
        const rawValue = webgl.customProps.readBuffer[j];
        outData[Math.round(j / channelCount)] = rawValue * 65535;
      }
    }

    const thermalData = {
      width: this.videoNode.width,
      height: this.videoNode.height,
      data: outData,
    };

    // eslint-disable-next-line
    // @ts-ignore
    this.imageToProcess = thermalData;
  };

  getImage = (cleanImage = true) => {
    const imageToProcessCopy = this.imageToProcess;

    if (cleanImage) {
      this.imageToProcess = null;
    }
    return imageToProcessCopy;
  };

  getStream = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevice = devices.filter((device) => device.kind === 'videoinput')[this.videoDeviceIndex];
    let stream = null;

    if (videoDevice.deviceId) {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: {
            exact: videoDevice.deviceId,
          },
          width: { exact: this.width },
          height: { exact: this.height },
        },
      });
    } else if (videoDevice.label) {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          // eslint-disable-next-line
          // @ts-ignore
          label: {
            exact: videoDevice.label,
          },
          width: { exact: this.width },
          height: { exact: this.height },
        },
      });
    } else if (videoDevice.groupId) {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          groupId: {
            exact: videoDevice.groupId,
          },
          width: { exact: this.width },
          height: { exact: this.height },
        },
      });
    } else {
      throw new Error(`Device data can't be received from the browser for videoDevice=${videoDevice}`);
    }

    console.log(` - Getting detailed data for video device`, videoDevice);
    if (stream) {
      const videoTrack = stream.getVideoTracks()[this.videoDeviceTrackIndex];

      if (videoTrack) {
        this.videoTrack = videoTrack;

        console.log(`   - VideoTrack:`, videoTrack);
        const videoTrackCapabilities = videoTrack.getCapabilities();
        console.log(`   - Capabilities:`, videoTrackCapabilities);
        const videoTrackSettings = videoTrack.getSettings();
        console.log(`   - Settings:`, videoTrackSettings);
      } else {
        throw new Error(`Additional device data can't be received from the browser for videoDevice=${videoDevice}`);
      }
    }
    return stream;
  };

  printAvailableVideoDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter((device) => device.kind === 'videoinput');

    const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
    console.log('Supported constraints', supportedConstraints);

    if (videoDevices && videoDevices.length > 0) {
      console.log(`There are ${videoDevices.length} video devices in the system:`);

      for (const videoDevice of videoDevices) {
        const videoDeviceInd = videoDevices.indexOf(videoDevice);
        let stream;

        if (videoDevice.deviceId) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: {
                exact: videoDevice.deviceId,
              },
              width: { exact: this.width },
              height: { exact: this.height },
            },
          });
        } else if (videoDevice.label) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              // eslint-disable-next-line
              // @ts-ignore
              label: {
                exact: videoDevice.label,
              },
              width: { exact: this.width },
              height: { exact: this.height },
            },
          });
        } else if (videoDevice.groupId) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              groupId: {
                exact: videoDevice.groupId,
              },
              width: { exact: this.width },
              height: { exact: this.height },
            },
          });
        } else {
          throw new Error(`Device data can't be received from the browser for videoDevice=${videoDevice}`);
        }

        console.log(`${videoDeviceInd + 1}) - Getting detailed data for video device`, videoDevice);
        if (stream) {
          const videoTracks = stream.getVideoTracks();

          if (videoTracks.length > 0) {
            console.log(`   - There are ${videoTracks.length} video track(s) for the video device`);
            videoTracks.forEach((videoTrack, index) => {
              console.log(`   - ${videoDeviceInd + 1}.${index + 1}.VideoTrack:`, videoTrack);
              const videoTrackCapabilities = videoTrack.getCapabilities();
              console.log(`   - ${videoDeviceInd + 1}.${index + 1}.Capabilities:`, videoTrackCapabilities);
              const videoTrackSettings = videoTrack.getSettings();
              console.log(`   - ${videoDeviceInd + 1}.${index + 1}.Settings:`, videoTrackSettings);
            });

            videoTracks.forEach((vt) => vt.stop());
          }
        }
      }
    }
  };
}

export default VideoStream;
